import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import IzinTalepFormu from '../components/forms/IzinTalepFormu';
import MasrafBildirimFormu from '../components/forms/MasrafBildirimFormu';
import { ROUTE_PATHS } from '../config/navigation';
import {
  hrSystems,
  onboardingChecklist,
  onboardingJourney
} from '../config/hrSystems';
import useDebouncedValue from '../hooks/useDebouncedValue';

const defaultStats = {
  totalEmployees: 0,
  activeLeaveRequests: 0,
  pendingApplications: 0,
  newEmployeesThisMonth: 0
};

const defaultCareerData = {
  opportunities: [],
  filters: {
    departments: [],
    listingTypes: [],
    workModels: []
  },
  summary: {
    total: 0,
    published: 0,
    urgentCount: 0
  }
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const HR_TAB_IDS = ['dashboard', 'izin-talep', 'masraf-bildirim'];

const HRPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [careerLoading, setCareerLoading] = useState(true);
  const [careerRefreshing, setCareerRefreshing] = useState(false);
  const [careerSearch, setCareerSearch] = useState('');
  const debouncedCareerSearch = useDebouncedValue(careerSearch, 350);
  const [careerFilters, setCareerFilters] = useState({
    department: '',
    listingType: '',
    workModel: ''
  });
  const [hrData, setHrData] = useState({
    stats: defaultStats,
    recentApplications: [],
    recentEmployees: [],
    newHires: []
  });
  const [careerData, setCareerData] = useState(defaultCareerData);
  const hasLoadedCareerRef = useRef(false);
  const careerRequestSequenceRef = useRef(0);
  const { toasts, addToast, removeToast } = useToast();

  useEffect(() => {
    const tabId = location.hash.replace('#', '');

    if (HR_TAB_IDS.includes(tabId)) {
      setActiveTab(tabId);
    }
  }, [location.hash]);

  const fetchHRData = useCallback(async () => {
    try {
      setLoading(true);

      const [statsResponse, applicationsResponse, recentEmployeesResponse, newHiresResponse] =
        await Promise.all([
          api.getHRStats(),
          api.getRecentApplications(),
          api.getRecentNewEmployees(),
          api.getNewHires({ limit: 6 })
        ]);

      setHrData({
        stats: statsResponse.data || defaultStats,
        recentApplications: applicationsResponse.data || [],
        recentEmployees: recentEmployeesResponse.data || [],
        newHires: newHiresResponse.employees || []
      });
    } catch (error) {
      console.error('HR data fetch error:', error);
      addToast('İK verileri yüklenemedi. Lütfen tekrar deneyin.', 'error', 4000);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const fetchCareerData = useCallback(async (filters, search) => {
    const requestId = ++careerRequestSequenceRef.current;
    try {
      if (hasLoadedCareerRef.current) setCareerRefreshing(true);
      else setCareerLoading(true);

      const response = await api.getCareerOpportunities({
        ...filters,
        ...(search.trim() && { search: search.trim() })
      });

      if (requestId === careerRequestSequenceRef.current) {
        setCareerData(response.data || defaultCareerData);
      }
    } catch (error) {
      if (requestId !== careerRequestSequenceRef.current) return;
      console.error('Career data fetch error:', error);
      addToast('Kariyer fırsatları yüklenemedi. Lütfen tekrar deneyin.', 'error', 4000);
    } finally {
      if (requestId === careerRequestSequenceRef.current) {
        hasLoadedCareerRef.current = true;
        setCareerLoading(false);
        setCareerRefreshing(false);
      }
    }
  }, [addToast]);

  useEffect(() => {
    fetchHRData();
  }, [fetchHRData]);

  useEffect(() => {
    fetchCareerData(careerFilters, debouncedCareerSearch);
  }, [careerFilters, debouncedCareerSearch, fetchCareerData]);

  const statsCards = useMemo(
    () => [
      {
        label: 'Toplam çalışan',
        value: hrData.stats.totalEmployees,
        icon: 'bx bxs-user',
        colorClass: 'primary'
      },
      {
        label: 'Aktif izinler',
        value: hrData.stats.activeLeaveRequests,
        icon: 'bx bx-calendar-minus',
        colorClass: 'success'
      },
      {
        label: 'Bekleyen başvurular',
        value: hrData.stats.pendingApplications,
        icon: 'bx bx-time-five',
        colorClass: 'warning'
      },
      {
        label: 'Bu ay işe başlayanlar',
        value: hrData.stats.newEmployeesThisMonth,
        icon: 'bx bx-user-plus',
        colorClass: 'info'
      }
    ],
    [hrData.stats]
  );

  const careerSummaryCards = useMemo(
    () => [
      {
        label: 'Yayındaki roller',
        value: careerData.summary.published,
        icon: 'bx bx-briefcase-alt-2'
      },
      {
        label: 'Filtre sonucu',
        value: careerData.summary.total,
        icon: 'bx bx-filter-alt'
      },
      {
        label: 'Öncelikli alımlar',
        value: careerData.summary.urgentCount,
        icon: 'bx bx-bolt-circle'
      }
    ],
    [careerData.summary]
  );

  const handleCareerFilterChange = (field, value) => {
    const nextFilters = {
      ...careerFilters,
      [field]: value
    };

    setCareerFilters(nextFilters);
  };

  const handleCareerSearch = (event) => {
    const nextValue = event.target.value;
    setCareerSearch(nextValue);
  };

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <h1>İnsan Kaynakları</h1>
        </div>

        <div className="modern-hr-tabs">
          <div className="d-flex justify-content-center gap-2 flex-wrap">
            <button
              className={`modern-hr-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <i className="bx bx-home-alt"></i>
              İK merkezi
            </button>
            <button
              className={`modern-hr-tab ${activeTab === 'izin-talep' ? 'active' : ''}`}
              onClick={() => setActiveTab('izin-talep')}
            >
              <i className="bx bx-calendar"></i>
              İzin talebi
            </button>
            <button
              className={`modern-hr-tab ${activeTab === 'masraf-bildirim' ? 'active' : ''}`}
              onClick={() => setActiveTab('masraf-bildirim')}
            >
              <i className="bx bx-receipt"></i>
              Masraf bildirimi
            </button>
          </div>
        </div>

        {activeTab === 'dashboard' && (
          <>
            {loading ? (
              <div className="modern-hr-loading">
                <i className="bx bx-loader-alt bx-spin"></i>
                <p>İK görünümü hazırlanıyor...</p>
              </div>
            ) : (
              <>
                <div className="modern-hr-stats">
                  {statsCards.map((card) => (
                    <div key={card.label} className="modern-hr-stat-card">
                      <div className={`modern-hr-stat-icon ${card.colorClass}`}>
                        <i className={card.icon}></i>
                      </div>
                      <div className="modern-hr-stat-number">{card.value}</div>
                      <div className="modern-hr-stat-label">{card.label}</div>
                    </div>
                  ))}
                </div>

                <div className="row">
                  <div className="col-xl-8 mb-4">
                    <div className="modern-hr-widget h-100">
                      <div className="modern-hr-widget-header">
                        <div className="modern-hr-widget-title">
                          <i className="bx bx-user-voice"></i>
                          <h4>Onboarding omurgası</h4>
                        </div>
                        <p className="modern-hr-widget-subtitle">
                          Yeni başlayan çalışanlar için ilk 30 günlük yapı ve görev sahipligi
                        </p>
                      </div>

                      <div className="row g-4">
                        <div className="col-lg-7">
                          <div className="hr-onboarding-panel">
                            <h5>İlk hafta kontrol listesi</h5>
                            <div className="hr-onboarding-list">
                              {onboardingChecklist.map((item) => (
                                <div key={item} className="hr-onboarding-list-item">
                                  <i className="bx bx-check-circle"></i>
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                            <div className="hr-onboarding-actions">
                              <Link className="modern-hr-btn modern-hr-btn-primary" to={ROUTE_PATHS.newEmployee}>
                                Yeni çalışan kaydı
                              </Link>
                              <Link className="modern-hr-btn modern-hr-btn-secondary" to={ROUTE_PATHS.documents}>
                                Oryantasyon dokümanları
                              </Link>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-5">
                          <div className="hr-onboarding-newhires">
                            <h5>Onboarding aşamaları</h5>
                            <div className="d-flex flex-column gap-3">
                              {onboardingJourney.map((step) => (
                                <div key={step.id} className="hr-job-card">
                                  <div className="hr-job-card-top">
                                    <span className="hr-job-badge">{step.timeline}</span>
                                    <small>{step.owner}</small>
                                  </div>
                                  <h5>{step.title}</h5>
                                  <p>{step.tasks.join(' • ')}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="row g-4 mt-1">
                        <div className="col-12">
                          <div className="hr-onboarding-newhires">
                            <h5>Son eklenen ekip arkadaşları</h5>
                            {hrData.newHires.length > 0 ? (
                              hrData.newHires.map((employee) => (
                                <div key={employee._id} className="hr-newhire-card">
                                  <div className="hr-newhire-avatar">
                                    {employee.firstName?.charAt(0)}
                                    {employee.lastName?.charAt(0)}
                                  </div>
                                  <div>
                                    <strong>
                                      {employee.firstName} {employee.lastName}
                                    </strong>
                                    <span>{employee.department}</span>
                                    <small>
                                      {employee.position} • {formatDate(employee.startDate)}
                                    </small>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="modern-hr-empty">
                                <i className="bx bx-user-plus"></i>
                                <p>Yeni çalışan kaydı bulunmuyor.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-xl-4 mb-4">
                    <div className="modern-hr-widget h-100">
                      <div className="modern-hr-widget-header">
                        <div className="modern-hr-widget-title">
                          <i className="bx bx-link-alt"></i>
                          <h4>İK sistemleri</h4>
                        </div>
                        <p className="modern-hr-widget-subtitle">
                          Dış sistem URL'leri merkezi konfigürasyonla yönetilir
                        </p>
                      </div>
                      <div className="hr-system-links">
                        {hrSystems.map((system) =>
                          system.isExternal ? (
                            <a
                              key={system.key}
                              href={system.href}
                              className="hr-system-link-card"
                              target="_blank"
                              rel="noreferrer"
                            >
                              <i className={system.icon}></i>
                              <div>
                                <strong>{system.title}</strong>
                                <span>{system.subtitle}</span>
                                <small>{system.ctaLabel}</small>
                              </div>
                            </a>
                          ) : (
                            <Link key={system.key} to={system.href} className="hr-system-link-card">
                              <i className={system.icon}></i>
                              <div>
                                <strong>{system.title}</strong>
                                <span>{system.subtitle}</span>
                                <small>{system.helperText}</small>
                              </div>
                            </Link>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xl-7 mb-4">
                    <div className="modern-hr-widget h-100 search-results-region" aria-busy={careerRefreshing}>
                      <div className="modern-hr-widget-header">
                        <div className="modern-hr-widget-title">
                          <i className="bx bx-briefcase-alt-2"></i>
                          <h4>İç ilanlar ve açık pozisyonlar</h4>
                        </div>
                        <p className="modern-hr-widget-subtitle">
                          Yönetilebilir veri kaynağından gelen güncel kariyer fırsatları
                        </p>
                      </div>

                      <div className="row g-3 mb-4">
                        {careerSummaryCards.map((card) => (
                          <div key={card.label} className="col-md-4">
                            <div className="hr-job-card h-100">
                              <div className="hr-job-card-top">
                                <span className="hr-job-badge">{card.label}</span>
                                <i className={card.icon}></i>
                              </div>
                              <h5>{card.value}</h5>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="row g-3 mb-4">
                        <div className="col-lg-4">
                          <input
                            className="modern-hr-form-input"
                            placeholder="Rol, departman veya yetkinlik ara"
                            value={careerSearch}
                            onChange={handleCareerSearch}
                          />
                        </div>
                        <div className="col-lg-3">
                          <select
                            className="modern-hr-form-select"
                            value={careerFilters.department}
                            onChange={(event) =>
                              handleCareerFilterChange('department', event.target.value)
                            }
                          >
                            <option value="">Tüm departmanlar</option>
                            {careerData.filters.departments.map((department) => (
                              <option key={department} value={department}>
                                {department}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-lg-3">
                          <select
                            className="modern-hr-form-select"
                            value={careerFilters.listingType}
                            onChange={(event) =>
                              handleCareerFilterChange('listingType', event.target.value)
                            }
                          >
                            <option value="">Tüm ilan tipleri</option>
                            {careerData.filters.listingTypes.map((listingType) => (
                              <option key={listingType} value={listingType}>
                                {listingType}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-lg-2">
                          <select
                            className="modern-hr-form-select"
                            value={careerFilters.workModel}
                            onChange={(event) =>
                              handleCareerFilterChange('workModel', event.target.value)
                            }
                          >
                            <option value="">Tüm modeller</option>
                            {careerData.filters.workModels.map((workModel) => (
                              <option key={workModel} value={workModel}>
                                {workModel}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {careerRefreshing && (
                        <div className="search-refresh-indicator" role="status">
                          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                          Sonuçlar güncelleniyor…
                        </div>
                      )}

                      {careerLoading ? (
                        <div className="modern-hr-loading">
                          <i className="bx bx-loader-alt bx-spin"></i>
                          <p>Kariyer fırsatları yükleniyor...</p>
                        </div>
                      ) : careerData.opportunities.length > 0 ? (
                        <div className="row g-3">
                          {careerData.opportunities.map((job) => (
                            <div key={job.id} className="col-md-6">
                              <div className="hr-job-card h-100">
                                <div className="hr-job-card-top">
                                  <span className="hr-job-badge">{job.listingType}</span>
                                  <small>{job.location}</small>
                                </div>
                                <h5>{job.title}</h5>
                                <strong>
                                  {job.department} • {job.workModel}
                                </strong>
                                <p>{job.summary}</p>
                                <div className="d-flex flex-wrap gap-2 mb-3">
                                  {job.skills.map((skill) => (
                                    <span key={skill} className="modern-hr-badge pending">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                                <small className="d-block text-muted mb-2">
                                  Son başvuru: {job.applicationWindow}
                                </small>
                                <Link to={job.applicationRoute} className="hr-job-link">
                                  Başvuru akışına git
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="modern-hr-empty">
                          <i className="bx bx-briefcase-alt"></i>
                          <p>Filtrelere uygun açık pozisyon bulunmuyor.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-xl-5 mb-4">
                    <div className="modern-hr-widget h-100">
                      <div className="modern-hr-widget-header">
                        <div className="modern-hr-widget-title">
                          <i className="bx bx-history"></i>
                          <h4>Son işe başlayanlar</h4>
                        </div>
                      </div>
                      {hrData.recentEmployees.length > 0 ? (
                        hrData.recentEmployees.map((employee, index) => (
                          <div key={`${employee.name}-${index}`} className="modern-hr-birthday-item">
                            <div className="modern-hr-birthday-avatar">
                              <i className="bx bx-user-plus"></i>
                            </div>
                            <div className="modern-hr-birthday-content">
                              <div className="modern-hr-birthday-name">{employee.name}</div>
                              <div className="modern-hr-birthday-department">
                                {employee.department} • {employee.position}
                              </div>
                              <div className="modern-hr-birthday-date">{employee.startDate}</div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="modern-hr-empty">
                          <i className="bx bx-user-plus"></i>
                          <p>Son işe başlayan kaydı bulunmuyor.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="modern-hr-widget">
                  <div className="modern-hr-widget-header">
                    <div className="modern-hr-widget-title">
                      <i className="bx bx-time-five"></i>
                      <h4>Bekleyen son başvurular</h4>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="modern-hr-table">
                      <thead>
                        <tr>
                          <th>Çalışan</th>
                          <th>Departman</th>
                          <th>Tür</th>
                          <th>Tarih</th>
                          <th>Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hrData.recentApplications.length > 0 ? (
                          hrData.recentApplications.map((application) => (
                            <tr key={application.id}>
                              <td>{application.employee}</td>
                              <td>{application.department}</td>
                              <td>{application.type}</td>
                              <td>{application.date}</td>
                              <td>
                                <span className="modern-hr-badge pending">{application.status}</span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              Bekleyen başvuru bulunmuyor.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'izin-talep' && (
          <div className="modern-hr-widget">
            <div className="modern-hr-widget-header">
              <div className="modern-hr-widget-title">
                <i className="bx bx-calendar"></i>
                <h4>İzin Talep Formu</h4>
              </div>
            </div>
            <IzinTalepFormu />
          </div>
        )}

        {activeTab === 'masraf-bildirim' && (
          <div className="modern-hr-widget">
            <div className="modern-hr-widget-header">
              <div className="modern-hr-widget-title">
                <i className="bx bx-receipt"></i>
                <h4>Masraf Bildirim Formu</h4>
              </div>
            </div>
            <MasrafBildirimFormu />
          </div>
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default HRPage;
